import { Injectable } from "@angular/core";

import { BfxBaseBlotterService } from "../../bfx-base-blotter/bfx-base-blotter.service";
import {
  InterventionDataPromiseLookupService,
  InterventionTableDefinitionFactory,
  ProfileSettingKeys,
  TableDefinition,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  BfxBlotterFilterId,
  BfxBlotterFilter,
  FilterType,
} from "app/bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxBlotterFilterService } from "app/bfx-blotter-filter/bfx-blotter-filter.service";
import { BlotterType, AssetClass, ProfileSettingImpl } from "shared/cubiq-model-multi-asset";
import { BfxBlotterInterventionFilterService } from "./bfx-blotter-intervention-filter.service";
import {
  GoldenLayoutService,
  GLPopoutConfiguration,
} from "app/golden-layout/golden-layout-service";
import { DashboardItemType } from "app/bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "app/golden-layout/golden-layout-addition-mode";
import { Subject } from "rxjs";
import { BfxUserProfileService } from "../../bfx-common/services/bfx-user-profile.service";

@Injectable()
export class BfxInterventionBlotterService extends BfxBaseBlotterService {
  private filterConfiguration = [];
  private filters: Array<BfxBlotterFilter> = [];
  private selectedFilters: Array<BfxBlotterFilter> = [];
  private interventionOrders: any = {};
  private component: any = null;

  public subjectNewItems: Subject<void> = new Subject<any>();
  public subjectUpdatedItems: Subject<string[]> = new Subject<any>();

  private readonly profileKey = `${ProfileSettingKeys.userProfileDefaultBlotter}${
    BlotterType[BlotterType.ORDER]
  }/INTERVENTION/SERVICE`;

  constructor(
    public dataService: InterventionDataPromiseLookupService,
    public tableDefinitionFactory: InterventionTableDefinitionFactory,
    public profileActionService: ProfilePromiseActionService,
    public interventionFilterService: BfxBlotterInterventionFilterService,
    public goldenLayoutService: GoldenLayoutService,
    protected filterService: BfxBlotterFilterService,
    protected userProfileService: BfxUserProfileService,
    private _systemConfigService: SystemConfigPromiseService,
  ) {
    super(dataService, tableDefinitionFactory);
    this.filterService.modelUpdateSubject.subscribe(this.refreshFilters.bind(this));
  }

  protected refreshFilters(): Promise<void> {
    return new Promise<void>((res, rej) => {
      this.filterService
        .getItems(
          FilterType.BlotterFilter,
          new BfxBlotterFilterId(BlotterType.INTERVENTION, AssetClass.FIX),
        )
        .then((filters: Array<BfxBlotterFilter>) => {
          this.filters = filters;
          res();
        });
    });
  }

  protected resolvePriming(res, rej): void {
    this.profileActionService.getProfileConfig(this.profileKey).then((value: string) => {
      let filterData: any = null;

      if (value != "" && value != null) filterData = JSON.parse(value);

      this.filterConfiguration = filterData != null ? filterData : [];

      this.refreshFilters().then(() => {
        this.tableDefinitionFactory.defineTable().then((tableDefinition: TableDefinition) => {
          this.updateFilterSelection(this.filterConfiguration);

          res();

          this._systemConfigService.canViewDealerInterventionBlotters().then((canSeeIt) => {
            if (canSeeIt && this.getItemCount() > 0) {
              setTimeout(this.openInterventionBlotter.bind(this), 4500);
            }
          });
        });
      });
    });
  }

  protected openInterventionBlotter(): void {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.preventActivation = true;
    this.goldenLayoutService.launchFromDashboard(
      "Dealer Intervention",
      DashboardItemType.InterventionBlotter,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  public updateFilterSelection(filter: Array<string>): void {
    let filterDataJson = "[]";
    this.filterConfiguration = filter;
    let profileSettings: Array<ProfileSettingImpl> = [];
    if (Array.isArray(filter)) filterDataJson = JSON.stringify(filter);

    profileSettings.push(
      this.userProfileService.createProfileSettingObject(this.profileKey, filterDataJson),
    );

    this.profileActionService.updateProfile(profileSettings, false).then(
      () => {},
      (reason) => {
        logger.out.warn(`${this.constructor.name}.updateFilterSelection() failed ${reason}`);
      },
    );

    this.selectedFilters = this.filters.filter(
      (fil: BfxBlotterFilter) => this.filterConfiguration.indexOf(fil.filterId) >= 0,
    );
    this.updateFilterData();
  }

  public updateFilterData(): void {
    Object.keys(this._rawData).forEach((key: any) => {
      if (this.doesExternalFilterPass(this._rawData[key])) {
        this.interventionOrders[key] = true;
      }
    });

    if (this.component != null) {
      this.updateBadgeCount(this.component);
    }
  }
  public doesExternalFilterPass(node: any): boolean {
    try {
      if (this.selectedFilters != null && this.selectedFilters.length > 0) {
        return this.interventionFilterService.doFiltersPass(node, this.selectedFilters);
      }
    } catch (error) {
      logger.out.error("Error while doing a filter pass.", error);
    }

    return true;
  }

  public getItemCount(): number {
    return Object.keys(this.interventionOrders).length;
  }

  protected onFullUpdateEvent(rows: Array<any>): void {
    super.onFullUpdateEvent(rows);

    // OMS-33523 multiline doesn't update even with
    // super.onFullUpdateEvent(rows, true);

    let hasNewRows: boolean = false;

    let updatedItemIds: Array<string> = [];

    rows.forEach((row: any) => {
      let orderId: string = row[this._tableDefinition.primaryKey];
      let wasIntervention: boolean = this.interventionOrders[orderId];
      let isIntervention: boolean = this.doesExternalFilterPass(row);

      if (!wasIntervention && isIntervention) {
        hasNewRows = true;
      }

      if (wasIntervention) {
        updatedItemIds.push(orderId);
      }

      if (isIntervention) {
        this.interventionOrders[orderId] = true;
      } else if (!isIntervention) {
        delete this.interventionOrders[orderId];
      }
    });

    if (updatedItemIds.length > 0) {
      this.subjectUpdatedItems.next(updatedItemIds);
    }

    if (hasNewRows) {
      this.openInterventionBlotter();
      this.subjectNewItems.next();
    }
  }

  protected onDeleteEvent(orderId: string): void {
    super.onDeleteEvent(orderId);
    delete this.interventionOrders[orderId];
  }

  public updateBadgeCount(component: any): void {
    this._ready.then(() => {
      if (this.component == null && component != null) {
        this.component = component;
        this.updateFilterData();
      }
      // this.goldenLayoutService.updateBadge(component, this.getItemCount());
    });
  }

  public getFilterConfiguration(): any {
    return this.filterConfiguration;
  }
}
